import { useEffect, useState } from "react";
import axios from "axios";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { RadioButton } from "primereact/radiobutton";
import localeText from "../../locale";
import FiscalService from "../../service/FiscalService";
import SubnetService from "../../service/SubnetService";
import { useToastContext } from "../../contexts/ToastContext/ToastContext";
import apiURI from "../../service/Api";

const PESSOA_FISICA = "FISICA";
const PESSOA_GIURIDICA = "GIURIDICA";

const initialFields = {
  firstname: "",
  surname: "",
  nomeFiscal: "",
  documentNumber: "",
  address: "",
  number: "",
  district: "",
  postalCode: "",
  city: "",
  state: "",
  country: "",
};

const FiscalDataGate = ({ userInfo }) => {
  const [visible, setVisible] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [tiposPessoaPermitidos, setTiposPessoaPermitidos] = useState([]);
  const [tipoPessoa, setTipoPessoa] = useState(null);
  const [fields, setFields] = useState(initialFields);
  const [errors, setErrors] = useState({});

  const { showErrorToast, showSuccessToast } = useToastContext();
  const fiscalService = new FiscalService();
  const subnetService = new SubnetService();

  useEffect(() => {
    let cancel = false;

    async function fetchStatus() {
      const token = localStorage.getItem("__extraconomy_token");
      if (!token) {
        setLoadingStatus(false);
        return;
      }

      try {
        const subnetInfo = await subnetService.getBelongsToSubnet(token);
        if (cancel) return;
        const isWhitelabel = subnetInfo !== null && Number(subnetInfo.id) !== 1;
        if (isWhitelabel) return;

        const res = await fiscalService.getFiscalStatus(token);
        if (cancel) return;

        if (res.messageCode !== 200001) {
          console.log(res);
          return;
        }

        const status = res.result;

        if (status?.complete === false) {
          const allowedTypes = status.tiposPessoaPermitidos || [];
          setTiposPessoaPermitidos(allowedTypes);
          setTipoPessoa(status.tipoPessoaAtual || allowedTypes[0] || null);
          setVisible(true);
        }
      } catch (e) {
        if (e?.response?.status === 401 || e?.response?.status === 403) {
          localStorage.removeItem("__extraconomy_token");
          localStorage.removeItem("__extraconomy_username");
          window.location.replace("/login");
        }
        console.log(e);
      } finally {
        if (!cancel) setLoadingStatus(false);
      }
    }

    fetchStatus();

    return () => {
      cancel = true;
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (userInfo?.country) {
      setFields((prev) => ({ ...prev, country: userInfo.country }));
    }
  }, [userInfo]);

  function getDocumentNumberLabel() {
    const isFisica = tipoPessoa === PESSOA_FISICA;
    const isGiuridica = tipoPessoa === PESSOA_GIURIDICA;
    const country = fields.country;

    if (country === "BR") {
      if (isFisica) return localeText("fiscalDocumentNumberCPF");
      if (isGiuridica) return localeText("fiscalDocumentNumberCNPJ");
    }

    if (country === "IT") {
      if (isFisica) return localeText("fiscalDocumentNumberCodiceFiscale");
      if (isGiuridica) return localeText("fiscalDocumentNumberPartitaIVA");
    }

    return localeText("fiscalDocumentNumber");
  }

  function setField(name, value) {
    setFields((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  }

  function validateForm() {
    const newErrors = {};
    const isFisica = tipoPessoa === PESSOA_FISICA;
    const isGiuridica = tipoPessoa === PESSOA_GIURIDICA;

    if (isFisica) {
      if (!fields.firstname.trim()) newErrors.firstname = localeText("pleaseFillOutAll");
      if (!fields.surname.trim()) newErrors.surname = localeText("pleaseFillOutAll");
    }

    if (isGiuridica) {
      if (!fields.nomeFiscal.trim()) newErrors.nomeFiscal = localeText("pleaseFillOutAll");
    }

    if (!fields.documentNumber.trim()) newErrors.documentNumber = localeText("pleaseFillOutAll");
    if (!fields.address.trim()) newErrors.address = localeText("pleaseFillOutAll");
    if (!fields.number.trim()) newErrors.number = localeText("pleaseFillOutAll");
    if (!fields.postalCode.trim()) newErrors.postalCode = localeText("pleaseFillOutAll");
    if (!fields.city.trim()) newErrors.city = localeText("pleaseFillOutAll");
    if (!fields.state.trim()) newErrors.state = localeText("pleaseFillOutAll");
    if (!fields.country.trim()) newErrors.country = localeText("pleaseFillOutAll");

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleLogout() {
    if (localStorage.getItem("__extraconomy_devicetoken__")) {
      try {
        const token = localStorage.getItem("__extraconomy_token");
        const username = localStorage.getItem("__extraconomy_username");
        const deviceId = localStorage.getItem("__extraconomy_devicetoken__");
        await axios({
          method: "post",
          url: `${apiURI}/device/unregisterdevice`,
          data: { user: { username }, deviceid: JSON.parse(deviceId) },
          headers: { Authorization: "Bearer " + token },
        });
      } catch (e) {}
    }

    if (JSON.parse(localStorage.getItem("__extraconomy_ismobile__") || "false")) {
      localStorage.removeItem("__extraconomy_token");
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(JSON.stringify({ isLogout: true }));
      }
    }

    localStorage.removeItem("__extraconomy_token");
    localStorage.removeItem("__extraconomy_username");
    localStorage.removeItem("__extraconomy_order");
    localStorage.removeItem("__extraconomy_order_fringe_benefit");
    window.location.replace("/login");
  }

  async function handleSubmit() {
    if (!validateForm()) return;

    setSubmitting(true);

    const isFisica = tipoPessoa === PESSOA_FISICA;
    const isGiuridica = tipoPessoa === PESSOA_GIURIDICA;

    const payload = {
      tipoPessoa,
      firstname: isFisica ? fields.firstname.trim() : null,
      surname: isFisica ? fields.surname.trim() : null,
      nomeFiscal: isGiuridica ? fields.nomeFiscal.trim() : null,
      documentNumber: fields.documentNumber.trim(),
      address: fields.address.trim(),
      number: fields.number.trim(),
      district: fields.district.trim() || null,
      postalCode: fields.postalCode.trim(),
      city: fields.city.trim(),
      state: fields.state.trim(),
      country: fields.country.trim(),
    };

    try {
      const token = localStorage.getItem("__extraconomy_token");
      const res = await fiscalService.postFiscalSave(token, payload);

      if (res.messageCode !== 200001) {
        showErrorToast({
          code: res.messageCode,
          message: res.message,
          fallbackMessage: localeText("fiscalGateUnexpectedError"),
        });
        return;
      }

      setVisible(false);
      showSuccessToast({ detail: "fiscalGateSuccess" });
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        localStorage.removeItem("__extraconomy_token");
        localStorage.removeItem("__extraconomy_username");
        window.location.replace("/login");
        return;
      }

      const apiMessage = e?.response?.data?.message;
      showErrorToast({ detail: apiMessage || "fiscalGateUnexpectedError" });
    } finally {
      setSubmitting(false);
    }
  }

  if (loadingStatus || !visible) return null;

  return (
    <Dialog
      header={localeText("fiscalGateTitle")}
      visible={visible}
      closable={false}
      closeOnEscape={false}
      dismissableMask={false}
      draggable={false}
      onHide={() => {}}
      breakpoints={{ "960px": "90vw" }}
      style={{ width: "50vw" }}
    >
      <p className="mb-4">{localeText("fiscalGateDescription")}</p>

      {tiposPessoaPermitidos.length > 1 && (
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2">{localeText("personType")} *</label>
          <div className="flex gap-4">
            <div className="flex align-items-center">
              <RadioButton
                inputId="tipoPessoaFisica"
                name="tipoPessoa"
                value={PESSOA_FISICA}
                onChange={(e) => setTipoPessoa(e.value)}
                checked={tipoPessoa === PESSOA_FISICA}
              />
              <label htmlFor="tipoPessoaFisica" className="ml-2">
                {localeText("personTypePhysical")}
              </label>
            </div>
            <div className="flex align-items-center">
              <RadioButton
                inputId="tipoPessoaGiuridica"
                name="tipoPessoa"
                value={PESSOA_GIURIDICA}
                onChange={(e) => setTipoPessoa(e.value)}
                checked={tipoPessoa === PESSOA_GIURIDICA}
              />
              <label htmlFor="tipoPessoaGiuridica" className="ml-2">
                {localeText("personTypeLegal")}
              </label>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-nogutter gap-3">
        {tipoPessoa === PESSOA_FISICA && (
          <>
            <div className="col-12 md:col-6">
              <label className="block text-sm font-medium mb-2">{localeText("firstName")} *</label>
              <InputText
                value={fields.firstname}
                onChange={(e) => setField("firstname", e.target.value)}
                className={`w-full ${errors.firstname ? "p-invalid" : ""}`}
              />
              {errors.firstname && <small className="p-error">{errors.firstname}</small>}
            </div>
            <div className="col-12 md:col-6">
              <label className="block text-sm font-medium mb-2">{localeText("surname")} *</label>
              <InputText
                value={fields.surname}
                onChange={(e) => setField("surname", e.target.value)}
                className={`w-full ${errors.surname ? "p-invalid" : ""}`}
              />
              {errors.surname && <small className="p-error">{errors.surname}</small>}
            </div>
          </>
        )}

        {tipoPessoa === PESSOA_GIURIDICA && (
          <div className="col-12">
            <label className="block text-sm font-medium mb-2">{localeText("nomeFiscal")} *</label>
            <InputText
              value={fields.nomeFiscal}
              onChange={(e) => setField("nomeFiscal", e.target.value)}
              className={`w-full ${errors.nomeFiscal ? "p-invalid" : ""}`}
            />
            {errors.nomeFiscal && <small className="p-error">{errors.nomeFiscal}</small>}
          </div>
        )}

        <div className="col-12">
          <label className="block text-sm font-medium mb-2">{getDocumentNumberLabel()} *</label>
          <InputText
            value={fields.documentNumber}
            onChange={(e) => setField("documentNumber", e.target.value)}
            className={`w-full ${errors.documentNumber ? "p-invalid" : ""}`}
          />
          {errors.documentNumber && <small className="p-error">{errors.documentNumber}</small>}
        </div>

        <div className="col-12 md:col-8">
          <label className="block text-sm font-medium mb-2">{localeText("fiscalAddress")} *</label>
          <InputText
            value={fields.address}
            onChange={(e) => setField("address", e.target.value)}
            className={`w-full ${errors.address ? "p-invalid" : ""}`}
          />
          {errors.address && <small className="p-error">{errors.address}</small>}
        </div>
        <div className="col-12 md:col-4">
          <label className="block text-sm font-medium mb-2">{localeText("number")} *</label>
          <InputText
            value={fields.number}
            onChange={(e) => setField("number", e.target.value)}
            className={`w-full ${errors.number ? "p-invalid" : ""}`}
          />
          {errors.number && <small className="p-error">{errors.number}</small>}
        </div>

        <div className="col-12">
          <label className="block text-sm font-medium mb-2">{localeText("district")}</label>
          <InputText
            value={fields.district}
            onChange={(e) => setField("district", e.target.value)}
            className="w-full"
          />
        </div>

        <div className="col-12 md:col-4">
          <label className="block text-sm font-medium mb-2">{localeText("zipCode")} *</label>
          <InputText
            value={fields.postalCode}
            onChange={(e) => setField("postalCode", e.target.value)}
            className={`w-full ${errors.postalCode ? "p-invalid" : ""}`}
          />
          {errors.postalCode && <small className="p-error">{errors.postalCode}</small>}
        </div>
        <div className="col-12 md:col-4">
          <label className="block text-sm font-medium mb-2">{localeText("city")} *</label>
          <InputText
            value={fields.city}
            onChange={(e) => setField("city", e.target.value)}
            className={`w-full ${errors.city ? "p-invalid" : ""}`}
          />
          {errors.city && <small className="p-error">{errors.city}</small>}
        </div>
        <div className="col-12 md:col-4">
          <label className="block text-sm font-medium mb-2">{localeText("state")} *</label>
          <InputText
            value={fields.state}
            onChange={(e) => setField("state", e.target.value)}
            className={`w-full ${errors.state ? "p-invalid" : ""}`}
          />
          {errors.state && <small className="p-error">{errors.state}</small>}
        </div>

        <div className="col-12">
          <label className="block text-sm font-medium mb-2">{localeText("country")} *</label>
          <InputText
            value={fields.country}
            disabled
            className={`w-full ${errors.country ? "p-invalid" : ""}`}
          />
          {errors.country && <small className="p-error">{errors.country}</small>}
        </div>
      </div>

      <div className="flex justify-content-between mt-4">
        <Button
          label={localeText("logout")}
          className="p-button-outlined p-button-secondary"
          onClick={handleLogout}
          disabled={submitting}
        />
        <Button label={localeText("save")} loading={submitting} onClick={handleSubmit} />
      </div>
    </Dialog>
  );
};

export default FiscalDataGate;
